import type { ReactNode } from "react";
import { useId } from "react";
import { cn } from "../utils/cx.js";
import { Field } from "./Field.js";

export type DateRange = [string, string];

export interface DateRangePreset {
  label: ReactNode;
  value: DateRange;
}

export interface DateRangePickerProps {
  /** [startISO, endISO] ("YYYY-MM-DD"). Controlled. */
  value?: DateRange;
  defaultValue?: DateRange;
  onChange?: (value: DateRange) => void;
  locale?: string;
  firstDay?: 0 | 1;
  min?: string;
  max?: string;
  presets?: DateRangePreset[];
  disabled?: boolean;
  allowClear?: boolean;
  placeholder?: [string, string];
  label?: ReactNode;
  help?: ReactNode;
  error?: ReactNode;
  required?: boolean;
  id?: string;
  className?: string;
}

const FIELD =
  "h-9 w-full rounded-md border border-input bg-transparent px-3 text-sm shadow-xs outline-none transition-[color,box-shadow] focus-visible:border-ring focus-visible:ring-ring/50 focus-visible:ring-[3px] disabled:cursor-not-allowed disabled:opacity-50 dark:bg-input/30";

/** Two native date inputs — preserves the [startISO, endISO] value contract. */
export function DateRangePicker(props: DateRangePickerProps) {
  const {
    value,
    defaultValue,
    onChange,
    min,
    max,
    disabled,
    presets,
    label,
    help,
    error,
    required,
    id,
    className,
  } = props;
  const auto = useId();
  const inputId = id ?? auto;
  const invalid = error != null;
  const start = value?.[0] ?? "";
  const end = value?.[1] ?? "";

  const control = (
    <div className={cn("flex flex-col gap-2", className)}>
      <div className="flex items-center gap-2">
        <input
          id={inputId}
          type="date"
          {...(value !== undefined
            ? { value: start }
            : defaultValue
              ? { defaultValue: defaultValue[0] }
              : {})}
          min={min}
          max={end || max}
          disabled={disabled}
          aria-invalid={invalid || undefined}
          onChange={(e) => onChange?.([e.target.value, end])}
          className={cn(FIELD, invalid && "border-destructive")}
        />
        <span className="shrink-0 text-muted-foreground">–</span>
        <input
          type="date"
          {...(value !== undefined ? { value: end } : defaultValue ? { defaultValue: defaultValue[1] } : {})}
          min={start || min}
          max={max}
          disabled={disabled}
          aria-invalid={invalid || undefined}
          onChange={(e) => onChange?.([start, e.target.value])}
          className={cn(FIELD, invalid && "border-destructive")}
        />
      </div>
      {presets != null && presets.length > 0 && (
        <div className="flex flex-wrap gap-1">
          {presets.map((p, i) => (
            <button
              key={i}
              type="button"
              onClick={() => onChange?.(p.value)}
              className="rounded-md border border-input px-2 py-1 text-xs hover:bg-accent hover:text-accent-foreground"
            >
              {p.label}
            </button>
          ))}
        </div>
      )}
    </div>
  );

  if (label != null || help != null || error != null) {
    return (
      <Field label={label} required={required} help={help} error={error} htmlFor={inputId}>
        {control}
      </Field>
    );
  }
  return control;
}
